import { useMemo, useState } from "react";
import { IconCheck, IconCoins, IconWallet } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { tools as allTools } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import { canPurchaseTool, toolLockReason, toolRequirement } from "../../engine/tools";
import { toolCategoryText } from "../../i18n/enumText";
import { useGameStore } from "../../state/store";
import type { Tool, ToolCategory } from "../../types";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { EmptyState } from "../common/EmptyState";
import { Panel } from "../common/Panel";
import { cx } from "../common/cx";
import { formatMoney } from "../common/format";
import { ToolManifest } from "./ToolManifest";
import { ToolPurchaseModal } from "./ToolPurchaseModal";
import { ToolLockChip, ToolReqMet } from "./ToolRequirement";
import styles from "./ToolsScreen.module.css";

export function ToolsScreen(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const player = useGameStore((state) => state.player);
  const purchaseTool = useGameStore((state) => state.purchaseTool);
  const setActiveTool = useGameStore((state) => state.setActiveTool);
  const navigateTo = useGameStore((state) => state.navigateTo);

  const [category, setCategory] = useState<ToolCategory | "all">("all");
  const [confirmTool, setConfirmTool] = useState<Tool | null>(null);

  const categories = useMemo(
    () => [...new Set(allTools.map((tool) => tool.category))],
    [],
  );
  const inCategory = (tool: Tool): boolean => category === "all" || tool.category === category;
  const ownedTools = allTools.filter(
    (tool) => player.ownedTools.includes(tool.id) && inCategory(tool),
  );
  const availableTools = allTools.filter(
    (tool) => !player.ownedTools.includes(tool.id) && inCategory(tool),
  );

  const firstBuyableId = availableTools.find((tool) => canPurchaseTool(player, tool))?.id;
  const firstTutorialToolId = firstBuyableId ?? availableTools[0]?.id;

  return (
    <Panel
      title={<Trans>Инструменты</Trans>}
      icon={<IconCoins size={18} />}
      actions={
        <span className={cx(styles.money, "mono")}>
          <IconCoins size={15} aria-hidden="true" />
          {formatMoney(player.money)}
        </span>
      }
    >
      <div className={styles.filters} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={category === "all"}
          className={cx(styles.filterTab, category === "all" && styles.filterActive)}
          onClick={() => setCategory("all")}
        >
          <Trans>Все</Trans>
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            role="tab"
            aria-selected={category === cat}
            className={cx(styles.filterTab, category === cat && styles.filterActive)}
            onClick={() => setCategory(cat)}
          >
            {i18n._(toolCategoryText(cat))}
          </button>
        ))}
      </div>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <Trans>В арсенале</Trans>
        </h2>
        {ownedTools.length === 0 ? (
          <EmptyState title={<Trans>Пока ничего не куплено</Trans>} />
        ) : (
          <div className={styles.grid}>
            {ownedTools.map((tool) => {
              const lockReason = toolLockReason(tool, player);
              return (
                <Card
                  key={tool.id}
                  className={cx(styles.card, lockReason && styles.locked)}
                  onClick={() => {
                    setActiveTool(tool.id);
                    navigateTo("tool_details");
                  }}
                >
                  <h3 className={styles.name}>
                  <button type="button" className={styles.nameButton}>
                    {t(tool.name)}
                  </button>
                </h3>
                  <p className={styles.description}>{t(tool.description)}</p>
                  <p className={styles.effect}>{t(tool.effect)}</p>
                  <ToolManifest category={tool.category} />
                  <div className={styles.foot}>
                    {lockReason ? (
                      <ToolLockChip requirement={lockReason} />
                    ) : (
                      <span className={styles.owned}>
                        <IconCheck size={15} aria-hidden="true" />
                        <Trans>В арсенале</Trans>
                      </span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <Trans>Доступно</Trans>
        </h2>
        <div className={styles.grid}>
          {availableTools.map((tool) => {
            const isUpcoming = tool.status === "upcoming";
            const affordable = canPurchaseTool(player, tool);
            const lockReason = toolLockReason(tool, player);
            const requirement = toolRequirement(tool);
            const lackLabel = formatMoney(tool.cost - player.money);
            return (
              <Card
                key={tool.id}
                className={cx(styles.card, lockReason && styles.locked)}
                data-tutorial={tool.id === firstTutorialToolId ? "tool-card" : undefined}
                onClick={() => {
                  setActiveTool(tool.id);
                  navigateTo("tool_details");
                }}
              >
                <h3 className={styles.name}>
                  <button type="button" className={styles.nameButton}>
                    {t(tool.name)}
                  </button>
                </h3>
                <p className={styles.description}>{t(tool.description)}</p>
                <p className={styles.effect}>{t(tool.effect)}</p>
                <ToolManifest category={tool.category} />
                {!isUpcoming && !lockReason && requirement && <ToolReqMet requirement={requirement} />}
                <div className={styles.foot}>
                  <span
                    className={cx(
                      styles.cost,
                      "mono",
                      !isUpcoming && !lockReason && player.money < tool.cost && styles.tooPricey,
                    )}
                  >
                    {formatMoney(tool.cost)}
                  </span>
                  <div className={styles.footRight}>
                    {isUpcoming ? (
                      <span className={styles.upcoming}>
                        <Trans>Скоро</Trans>
                      </span>
                    ) : lockReason ? (
                      <ToolLockChip requirement={lockReason} />
                    ) : (
                      <Button
                        variant="primary"
                        size="small"
                        disabled={!affordable}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmTool(tool);
                        }}
                      >
                        <Trans>Купить</Trans>
                      </Button>
                    )}
                  </div>
                </div>
                {!isUpcoming && !lockReason && player.money < tool.cost && (
                  <p className={styles.lackNote}>
                    <IconWallet size={13} aria-hidden="true" />
                    <Trans>Не хватает {lackLabel}</Trans>
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      {confirmTool && (
        <ToolPurchaseModal
          tool={confirmTool}
          money={player.money}
          onConfirm={() => {
            purchaseTool(confirmTool.id);
            setConfirmTool(null);
          }}
          onClose={() => setConfirmTool(null)}
        />
      )}
    </Panel>
  );
}
